import React, { useState } from 'react'
import { v1 as uuid } from 'uuid'
import { Dashboard as DashboardIcon } from '@material-ui/icons'
import { useQuery } from '@apollo/react-hooks'
import { Button } from '@jarden-digital/component-lib'
import { IGetStaticException, QUERY_STATIC_EXCEPTIONS, QUERY_STATIC_COUNTS, IGetStaticCounts } from '../api/static-exceptions'
import { Card, ScreenTitle, Filters, IFilterSelectEntry, createEmptyFilter } from '../components'
import { SExceptionsTable } from '../contexts'
import { FilterDataTypes, IFilter, FilterCommand } from '../utils/filter'
import { downloadCSV, getCountForName, ICounter } from '../utils/common'


const cards = [
    { type: 'all', title: 'All Exceptions' },
    { type: 'no-ird', title: 'Missing IRD' },
    { type: 'invalid-ird', title: 'Invalid IRD' },
    { type: 'no-2nd-ird', title: 'Missing IRD for 2nd person' },
    { type: 'invalid-2nd-ird', title: 'Invalid IRD for 2nd person' },
    { type: 'equal-irds', title: 'Same IRD for both contacts' },
    { type: 'no-email-address', title: 'Missing E-mail address' },
    { type: 'invalid-email-address', title: 'Invalid E-mail address' },
    { type: 'invalid-address', title: 'Invalid Address' },
    { type: 'no-country-code', title: 'Missing Country Code' },
    { type: 'invalid-country-code', title: 'Invalid Country Code' },
    { type: 'no-tax-rate', title: 'Missing Tax Rate' },
    { type: 'no-2nd-name', title: 'Missing name for 2nd person' }
]

const fields: IFilterSelectEntry[] = [
    { label: 'CP Code', value: 'cp_code', dataType: FilterDataTypes.string },
    { label: 'Name', value: 'name', dataType: FilterDataTypes.string },
    { label: 'TIN', value: 'ird', dataType: FilterDataTypes.string },
    { label: '2nd TIN', value: '2nd-ird', dataType: FilterDataTypes.string },
    { label: 'Address', value: 'address', dataType: FilterDataTypes.string },
    { label: 'Country', value: 'country', dataType: FilterDataTypes.string },
    { label: 'Email', value: 'email_address', dataType: FilterDataTypes.string },
    { label: 'Tax Rate', value: 'rate', dataType: FilterDataTypes.string },
    { label: '2nd Name', value: '2nd-name', dataType: FilterDataTypes.string }
]

const SExceptionsList: React.FunctionComponent<{ filters: IFilter[], totalCount: number }> = ({ filters, totalCount }) => {

    const { loading, error, data, fetchMore } = useQuery<IGetStaticException>(QUERY_STATIC_EXCEPTIONS, {
        variables: {
            limit: 100,
            offset: 0,
            filters: filters
        }
    });

    if (loading) return <p>Loading...</p>
    if (error) return <p>Error :(</p>

    const loadMore = () => {
        return fetchMore({
            variables: { offset: data?.static_exceptions.length },
            updateQuery: (prev, { fetchMoreResult }) => {
                return !fetchMoreResult ? prev : Object.assign({}, prev, {
                    static_exceptions: [...prev.static_exceptions, ...fetchMoreResult.static_exceptions]
                })
            }
        })
    }

    const exceptions = data?.static_exceptions || []

    return (
        <div className="grid">
            <div className="gridContents table" style={{ position: 'relative' }}>
                <SExceptionsTable exceptions={exceptions} />
                <div className="gridContents" style={{textAlign:'center', width: `100%`}}>
                    <p className="label" style={{marginBottom: 16, marginTop: 32, textAlign:'center', width: `100%`}}>
                        Showing <strong>{exceptions.length}</strong> Rows of <strong>{totalCount}</strong> Total Results
                    </p>
                    <Button onClick={loadMore}>Load More</Button>
                </div>
            </div>
        </div>
    )
}

// const createCounter = (counter: number) => {
//     return (
//         <div className="grid">
//             <span className="gridContents label" style={{marginBottom: 8}}>
//                 <strong>{counter}</strong> results
//             </span>
//         </div>
//     )
// }

const getCardFilters = (totalCounts: ICounter[],
    cardFilter: string,
    setCardFilter: (card: string) => void, 
    filterDraft: IFilter[], 
    filterApplied: IFilter[], 
    setFilterDraft: (f: IFilter[]) => void, 
    setFilterApplied: (f: IFilter[]) => void) => {

    const changeCardFilter = (cardFilter: string) => {
        const drafts = filterDraft.filter(f => !f.extraType)
        const applied = filterApplied.filter(f => !f.extraType)
        const typeFilter = {
            id: uuid(),
            field: 'exception',
            command: FilterCommand.is,
            value: cardFilter,
            extraType: 'card'
        }
        if (cardFilter === 'all') {
            setFilterDraft(drafts)
            setFilterApplied(applied)
        } else {
            setFilterDraft([...drafts, typeFilter])
            setFilterApplied([...applied, typeFilter])
        }
        setCardFilter(cardFilter)
    }

    return (
        <div className="grid">
            <div className="containFilters gridContents">
                {cards.map(card => (
                    <Card key={card.type}
                        title={card.title}
                        count={getCountForName(totalCounts, card.type)}
                        active={cardFilter === card.type}
                        slim={true}
                        onClick={() => changeCardFilter(card.type)} />
                ))}
            </div>
        </div>
    )
}

export const SExceptionsView: React.FunctionComponent = () => {

    const [cardFilter, setCardFilter] = useState('all')
    const [filterDraft, setFilterDraft] = useState<IFilter[]>([createEmptyFilter()])
    const [filterApplied, setFilterApplied] = useState<IFilter[]>([])

    const { loading, error, data } = useQuery<IGetStaticCounts>(QUERY_STATIC_COUNTS, {
        variables: { names: cards.map(c => c.type) }
    });

    if (loading) return <p>Loading...</p>
    if (error) return <p>Error :(</p>

    const counters = data?.static_counts || []

    const onExportClick = () => downloadCSV('static_exceptions.csv', { offset: 0, limit: 1000000, filters: filterApplied })

    return (
        <main>
            <section>
                <ScreenTitle
                    title="Static Exceptions"
                    onExportClick={onExportClick}
                    icon={<DashboardIcon />}
                    arrowNaviagteTo="/" />

                {getCardFilters(counters, cardFilter, setCardFilter, filterDraft, filterApplied, setFilterDraft, setFilterApplied)}
                {/* {createCounter(getCountForName(counters, cardFilter))} */}
                <Filters fields={fields}
                    filterDraft={filterDraft} filterApplied={filterApplied}
                    setFilterDraft={setFilterDraft} setFilterApplied={setFilterApplied} />
                <SExceptionsList filters={filterApplied} totalCount={getCountForName(counters, cardFilter)} />
            </section>
        </main>
    )
}
